import {
  getDoc,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { getCareAppUserForPatientQueries, getEffectiveTenantScope } from './patientService';
import { getEffectiveTenantId, tenantCollection, tenantDoc, tenantPatientCollection, tenantPatientDoc } from './tenantContext';
import { blocksNewAssignment } from './pathwayDueUtils';

async function patientEffectiveTenantId(patientId) {
  const tid = await getEffectiveTenantId();
  return tid || 'trilogy';
}

/**
 * Fetch pathways that are active (for assignment dropdown), scoped by effective tenant (Phase 4).
 * Uses tenant-filtered Firestore queries when impersonating (or org user) so rules allow the query.
 */
export async function getActivePathways() {
  const u = await getCareAppUserForPatientQueries();
  const scope = getEffectiveTenantScope(u);
  if (scope.mode === 'none') return [];

  const pathwaysRef = tenantCollection(scope.tenantId, 'pathways');
  const q = query(pathwaysRef, where('active', '==', true));

  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Fetch all pathway assignments for a patient (active and cancelled).
 */
export async function getAssignmentsForPatient(patientId) {
  if (!patientId) return [];
  const tenantId = await patientEffectiveTenantId(patientId);
  const q = query(
    tenantPatientCollection(tenantId, patientId, 'pathwayAssignments')
  );
  const snapshot = await getDocs(q);
  const list = snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
    assignedAt: d.data().assignedAt?.toDate?.() ?? d.data().assignedAt,
    completedAt: d.data().completedAt?.toDate?.() ?? d.data().completedAt,
    cancelledAt: d.data().cancelledAt?.toDate?.() ?? d.data().cancelledAt,
    lastCompletedAt: d.data().lastCompletedAt?.toDate?.() ?? d.data().lastCompletedAt,
  }));
  list.sort((a, b) => (b.assignedAt?.getTime?.() ?? 0) - (a.assignedAt?.getTime?.() ?? 0));
  return list;
}

/**
 * Fetch all pathwayProgress docs for a patient (used by care Patient To-Do's
 * to mirror which question instances are currently due on the patient Home).
 */
export async function getPathwayProgressForPatient(patientId) {
  if (!patientId) return [];
  const tenantId = await patientEffectiveTenantId(patientId);
  const snapshot = await getDocs(
    query(tenantPatientCollection(tenantId, patientId, 'pathwayProgress')),
  );
  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
    dueAt: d.data().dueAt?.toDate?.() ?? d.data().dueAt,
    startedAt: d.data().startedAt?.toDate?.() ?? d.data().startedAt,
    completedAt: d.data().completedAt?.toDate?.() ?? d.data().completedAt,
    closedAt: d.data().closedAt?.toDate?.() ?? d.data().closedAt,
  }));
}

const AUDIT_COLLECTION = 'pathwayAssignmentAuditLog';

function writeAuditLog({ tenantId, userId, action, assignmentId, patientId, pathwayId, payload = null }) {
  return addDoc(tenantCollection(tenantId, AUDIT_COLLECTION), {
    userId,
    action,
    assignmentId: assignmentId || null,
    patientId: patientId || null,
    pathwayId: pathwayId || null,
    payload,
    timestamp: serverTimestamp(),
  });
}

/**
 * If the pathway is "education-only" (root step is final and has assign-education actions),
 * create educationAssignments and a completed pathwayProgress so the patient never sees a question.
 *
 * Only runs for manual `now` assignments — for future/recurring/alwaysOn the patient app and
 * cloud functions kick off pathway runs on the right schedule.
 *
 * @param {string} assignmentId - pathwayAssignments doc id
 * @param {string} patientId
 * @param {string} pathwayId
 * @param {string} assignedBy - care user uid
 */
async function processEducationOnlyPathwayAssignment(assignmentId, patientId, pathwayId, assignedBy) {
  const tenantId = await patientEffectiveTenantId(patientId);
  const pathwaySnap = await getDoc(tenantDoc(tenantId, 'pathways', pathwayId));
  if (!pathwaySnap.exists()) return;
  const pathway = { id: pathwaySnap.id, ...pathwaySnap.data() };
  const rootStepId = pathway.rootStepId;
  if (!rootStepId) return;

  const stepsSnap = await getDocs(
    query(tenantCollection(tenantId, 'pathwaySteps'), where('pathwayId', '==', pathwayId))
  );
  const steps = stepsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const rootStep = steps.find((s) => s.id === rootStepId);
  if (!rootStep || !rootStep.isFinalStep) return;

  const stepActions = Array.isArray(rootStep.actions) ? rootStep.actions : [];
  const branchActions = (rootStep.branches || []).flatMap((b) => b.actions || []);
  const educationActions = [...stepActions, ...branchActions].filter(
    (a) => a && a.type === 'assignEducationOnCompletion' && a.config && a.config.educationId
  );
  if (educationActions.length === 0) return;

  const safeKey = (s) =>
    String(s ?? '')
      .replace(/[^a-zA-Z0-9_-]/g, '_')
      .slice(0, 80);

  for (const action of educationActions) {
    const educationId = action.config.educationId;
    const docId = `pathway_${safeKey(patientId)}_${safeKey(assignmentId)}_${safeKey(educationId)}`;
    await setDoc(tenantPatientDoc(tenantId, patientId, 'educationAssignments', docId), {
      patientId,
      tenantId,
      educationId,
      assignedBy: assignedBy || 'system:pathway',
      assignedAt: serverTimestamp(),
      dueAt: serverTimestamp(),
      status: 'assigned',
      completedAt: null,
      completedBy: null,
      source: 'pathway',
      pathwayId,
      pathwayAssignmentId: assignmentId,
    }, { merge: true });
  }

  await updateDoc(tenantPatientDoc(tenantId, patientId, 'pathwayAssignments', assignmentId), {
    completedByPatient: true,
    completedAt: serverTimestamp(),
    lastCompletedAt: serverTimestamp(),
    active: false,
  });

  await addDoc(tenantPatientCollection(tenantId, patientId, 'pathwayProgress'), {
    patientId,
    tenantId,
    pathwayId,
    pathwayAssignmentId: assignmentId,
    dueAt: serverTimestamp(),
    startedAt: serverTimestamp(),
    currentStepId: rootStepId,
    answers: [],
    completedAt: serverTimestamp(),
  });
}

/**
 * Coerce a free-form value into a Firestore Timestamp.
 *  - already a Timestamp → returned as-is
 *  - Date instance → converted
 *  - ISO/string → parsed
 *  - falsy / invalid → null
 */
function toTimestamp(value) {
  if (!value) return null;
  if (value instanceof Timestamp) return value;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : Timestamp.fromDate(value);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
}

const VALID_SCHEDULE_KINDS = new Set(['now', 'futureAt', 'recurring']);
const VALID_OCCURRENCE_TYPES = new Set(['daily', 'weekly', 'monthly']);

/**
 * Validate and normalize a manual-schedule payload coming from the assign UI.
 * Returns the persisted shape or throws when invalid.
 *
 * @param {{ kind: string, futureAt?: any, recurring?: { occurrences?: any[] } }} schedule
 */
function normalizeManualSchedule(schedule) {
  const kind = schedule?.kind;
  if (!VALID_SCHEDULE_KINDS.has(kind)) {
    throw new Error('Schedule kind must be "now", "futureAt", or "recurring".');
  }
  if (kind === 'now') return { kind: 'now' };

  if (kind === 'futureAt') {
    const ts = toTimestamp(schedule.futureAt);
    if (!ts) throw new Error('Future date/time is required.');
    if (ts.toMillis() <= Date.now()) {
      throw new Error('Future date/time must be in the future.');
    }
    return { kind: 'futureAt', futureAt: ts };
  }

  /** Recurring */
  const occurrences = Array.isArray(schedule?.recurring?.occurrences)
    ? schedule.recurring.occurrences
    : [];
  if (occurrences.length === 0) {
    throw new Error('Recurring schedules need at least one occurrence row.');
  }
  const cleaned = [];
  for (const o of occurrences) {
    if (!o || !VALID_OCCURRENCE_TYPES.has(o.type)) {
      throw new Error('Each occurrence must be daily, weekly, or monthly.');
    }
    const time = String(o.time || '').trim();
    if (!/^\d{1,2}:\d{2}$/.test(time)) {
      throw new Error('Each occurrence needs a HH:MM time.');
    }
    const row = { type: o.type, time };
    if (o.type === 'weekly') {
      const dow = Number(o.dayOfWeek);
      if (!Number.isInteger(dow) || dow < 0 || dow > 6) {
        throw new Error('Weekly occurrences need a day of week (0=Sun … 6=Sat).');
      }
      row.dayOfWeek = dow;
    }
    if (o.type === 'monthly') {
      const dom = Number(o.dayOfMonth);
      if (!Number.isInteger(dom) || dom < 1 || dom > 31) {
        throw new Error('Monthly occurrences need a day of month (1–31).');
      }
      row.dayOfMonth = dom;
    }
    cleaned.push(row);
  }
  return { kind: 'recurring', recurring: { occurrences: cleaned } };
}

/**
 * Lookup an already-active assignment for (patient, pathway) that blocks a new
 * one — used to enforce the "max one Always-On per pathway" rule for
 * reading-triggered assignments and the manual duplicate guard. Completed
 * one-time manual assignments never block.
 */
async function findActiveAssignmentFor(tenantId, patientId, pathwayId) {
  const q = query(
    tenantPatientCollection(tenantId, patientId, 'pathwayAssignments'),
    where('pathwayId', '==', pathwayId),
    where('active', '==', true),
  );
  const snap = await getDocs(q);
  const blocking = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .find((a) => blocksNewAssignment(a));
  return blocking || null;
}

/**
 * Create a new pathway assignment in the redesigned shape.
 *
 * Reading-triggered assignments are Always-On until cancelled or the patient is
 * deactivated. They never have a schedule, never have autoReplace, and the
 * caller's `schedule` payload (if any) is ignored. Uniqueness is enforced:
 * only one active reading assignment per (patient, pathway).
 *
 * Manual assignments require `schedule.kind` and optionally `autoReplace`
 * (recurring only, defaults true).
 *
 * @param {Object} params
 * @param {string} params.patientId
 * @param {string} params.pathwayId
 * @param {string} params.pathwayName
 * @param {'manual'|'reading'} params.triggerSource
 * @param {string} params.assignedBy
 * @param {string} [params.assignedByName]
 * @param {Object} [params.schedule] - { kind, futureAt?, recurring? } (manual only)
 * @param {boolean} [params.autoReplace] - recurring only (default true)
 * @param {string|null} [params.pathwayGroupId]
 * @param {string|null} [params.pathwayGroupName]
 */
export async function createAssignment({
  patientId,
  pathwayId,
  pathwayName,
  triggerSource,
  assignedBy,
  assignedByName = '',
  schedule = null,
  autoReplace = true,
  pathwayGroupId = null,
  pathwayGroupName = null,
}) {
  if (!patientId) throw new Error('patientId is required.');
  if (!pathwayId) throw new Error('pathwayId is required.');
  const source = triggerSource === 'reading' ? 'reading' : 'manual';
  const tenantId = await patientEffectiveTenantId(patientId);

  const payload = {
    patientId,
    tenantId,
    pathwayId,
    pathwayName: pathwayName || '',
    triggerSource: source,
    assignedBy,
    assignedAt: serverTimestamp(),
    active: true,
    completedByPatient: false,
    completedAt: null,
    lastCompletedAt: null,
  };

  if (source === 'reading') {
    const existing = await findActiveAssignmentFor(tenantId, patientId, pathwayId);
    if (existing) {
      throw new Error(
        'This reading-triggered pathway is already Always-On for the patient. Cancel the existing assignment first.'
      );
    }
    payload.schedule = null;
  } else {
    /**
     * Manual duplicate guard: stacking two active manual assignments for the
     * same (patient, pathway) silently doubles the patient-side question
     * count and leaves both schedules running in parallel. Force the care
     * user to cancel the existing one first.
     */
    const existing = await findActiveAssignmentFor(tenantId, patientId, pathwayId);
    if (existing) {
      throw new Error(
        'This pathway is already actively assigned to the patient. Cancel the existing assignment first if you want to reschedule.'
      );
    }
    const normalized = normalizeManualSchedule(schedule || { kind: 'now' });
    payload.schedule = normalized;
    if (normalized.kind === 'recurring') {
      payload.autoReplace = autoReplace !== false;
    }
  }

  const nameTrimmed = String(assignedByName || '').trim();
  if (nameTrimmed) payload.assignedByName = nameTrimmed;

  /**
   * Stamp the originating Pathway Group on the assignment doc (not just the
   * audit log) so group-level actions ("apply schedule to existing patients",
   * deactivate-with-cascade) can target exactly the assignments the group
   * created. The patient app ignores these fields.
   */
  if (pathwayGroupId) {
    payload.pathwayGroupId = pathwayGroupId;
    payload.pathwayGroupName = pathwayGroupName || '';
  }

  const ref = await addDoc(
    tenantPatientCollection(tenantId, patientId, 'pathwayAssignments'),
    payload,
  );

  const auditPayload = {
    pathwayName: payload.pathwayName,
    triggerSource: source,
    scheduleKind: source === 'reading' ? 'alwaysOn' : payload.schedule.kind,
    autoReplace: source === 'reading' ? null : (payload.autoReplace ?? null),
  };
  if (pathwayGroupId) auditPayload.pathwayGroupId = pathwayGroupId;
  if (pathwayGroupName) auditPayload.pathwayGroupName = pathwayGroupName;

  await writeAuditLog({
    tenantId,
    userId: assignedBy,
    action: 'created',
    assignmentId: ref.id,
    patientId,
    pathwayId,
    payload: auditPayload,
  });

  /**
   * Only "now" manual assignments trigger the immediate education side-effect.
   * Future/recurring run when their schedule fires (patient app's
   * getCurrentDueProgress); alwaysOn is server-driven by onReadingCreated.
   */
  if (source === 'manual' && payload.schedule.kind === 'now') {
    await processEducationOnlyPathwayAssignment(ref.id, patientId, pathwayId, assignedBy);
  }

  return ref.id;
}

/**
 * Cancel an assignment and close any in-flight pathwayProgress for it so the
 * patient app immediately stops surfacing the now-stale run. The matching
 * Cloud Function (`onPatientAccessWritten` / `onPathwayWritten`) performs the
 * same cleanup defensively in case the client never gets the chance.
 *
 * @param {string} patientId
 * @param {string} assignmentId
 * @param {Object} [options]
 * @param {string} [options.userId] - care user uid who cancelled
 * @param {'user'|'patient_deactivated'|'pathway_deactivated'} [options.reason]
 * @param {string} [options.patientId]
 * @param {string} [options.pathwayId]
 */
export async function cancelAssignment(patientId, assignmentId, options = {}) {
  const tenantId = await patientEffectiveTenantId(patientId);
  const reason = options.reason || 'user';
  await updateDoc(tenantPatientDoc(tenantId, patientId, 'pathwayAssignments', assignmentId), {
    active: false,
    cancelledAt: serverTimestamp(),
    cancelledReason: reason,
  });

  /**
   * Close any open progress records keyed to this assignment so the patient's
   * queue refreshes without a round trip through the Cloud Function.
   */
  try {
    const progressSnap = await getDocs(
      query(
        tenantPatientCollection(tenantId, patientId, 'pathwayProgress'),
        where('pathwayAssignmentId', '==', assignmentId),
      ),
    );
    const openDocs = progressSnap.docs.filter((d) => !d.data()?.completedAt);
    for (const d of openDocs) {
      await updateDoc(d.ref, {
        closedAt: serverTimestamp(),
        closedReason: 'assignment_cancelled',
        completedAt: serverTimestamp(),
      });
    }
  } catch (e) {
    console.warn('cancelAssignment: failed to close in-flight progress', e?.message || e);
  }

  if (options.userId != null) {
    await writeAuditLog({
      tenantId,
      userId: options.userId,
      action: 'cancelled',
      assignmentId,
      patientId: options.patientId || null,
      pathwayId: options.pathwayId || null,
      payload: { cancelledReason: reason },
    });
  }
}

/**
 * Fetch audit log entries for a patient's pathway assignments (for display in care app).
 */
export async function getAssignmentAuditLogForPatient(patientId, limit = 50) {
  if (!patientId) return [];
  const tenantId = await patientEffectiveTenantId(patientId);
  const q = query(
    tenantCollection(tenantId, AUDIT_COLLECTION),
    where('patientId', '==', patientId)
  );
  const snapshot = await getDocs(q);
  const list = snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
    timestamp: d.data().timestamp?.toDate?.() ?? d.data().timestamp,
  }));
  list.sort((a, b) => (b.timestamp?.getTime?.() ?? 0) - (a.timestamp?.getTime?.() ?? 0));
  return list.slice(0, limit);
}
